import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation } from '@tanstack/react-query';

import type { UntiApplicationChangeDto, UntiApplicationDto, UntiApplicationOurDataDto } from '../../api/unti';
import { useUntiApplicationDetail, useUntiApplications, useUntiTransfertValues } from '../../api/unti';
import { requestUntiSyncRun, useSystemStatus, type Unti2035StatusDto } from '../../api/system-status';
import { Badge, Button, Drawer, EmptyState, ErrorState, Input, Pagination, Select } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { useQueryParams, type ParamValue } from '../../lib/query-params';
import { usePrograms } from '../quotas/dictionary';
import {
  OUR_CONTRACT_OPTIONS,
  PARAM,
  TABLE_HEADS,
  type SortableColumn,
  EXIT_EXAM_COLUMN,
  UNTI_FIELD_GROUPS,
  UNTI_FILTER_GROUPS,
  SERVER_SORT_HINT,
  exitExamFilterOptions,
  exitExamStatusView,
  TRANSFERT_FILTER_NONE,
  TRANSFERT_NONE_LABEL,
  applicationsRegisterLink,
  clampPage,
  filtersToQuery,
  formatChangeValue,
  formatDateTime,
  formatFieldValue,
  formatSortState,
  formatCount,
  hasOurFilters,
  isDefaultSort,
  looksLikeDatetime,
  ourContractView,
  ourEviFailedLabel,
  ourFormatLabel,
  ourStatusFilterOptions,
  ourStatusView,
  pageCount,
  parseFilters,
  parseIntParam,
  untiTransfertSelectOptions,
  sortArrow,
  statusFilterOptions,
  statusTone,
  toggleSort,
  untiWidgets,
} from './unti-model';

import './unti.css';

/** The manual poll cadence while an out-of-band sweep is armed (the worker's
 *  drain poll answers within ~30 s; #23 itself re-polls every 30 s). */
const SYNC_POLL_MS = 10_000;
/** The arm gives up RENDERING «идёт синхронизация…» after five minutes — the
 *  sweep may be stuck; the note says so instead of lying for ever. */
const SYNC_POLL_MAX_MS = 5 * 60_000;

/**
 * «Данные УНТИ» (T.U2) — the ADMIN-only page over the mirror T.U1 shipped:
 * ALL the data the УНТИ API holds, store-as-is, with filters, pagination, a
 * per-row DETAILS drawer and the top widgets with the numbers.
 *
 *   * the whole view lives in the URL (the dashboard's rule) — filters, page
 *     and the drawer's `?id=` restore on a refresh or a shared link;
 *   * #36 filters, paginates and — since v1.9.43 (T.Z154) — ORDERS: a header
 *     click writes `?sortBy=&order=` into the URL, the query carries #36's
 *     `sort`/`dir`, and the api returns the WHOLE filtered set reordered (the
 *     owner's ask: sorting must cover the data, not the 20 rows on screen).
 *     No client-side sort is left here — the page renders the slice it got;
 *   * the course caption comes from our `programs` dictionary (#21) — one id
 *     space with the mirror's TEXT `courseId`, so an unknown id prints raw;
 *   * the drawer is the row's 39 fields in the five owner groups + the change
 *     history of #37 + the link to OUR register (`?q=<unti_id>`, verified
 *     against the api's `q` field list — see `applicationsRegisterLink`).
 *
 * ADDITIVE (v1.9.40, T.Z147) — the page grew OUR half: the «Наш статус»/
 * «Формат»/«Договор»/«Провалил ЕВИ» columns read the row's `our` block (the
 * LEFT JOIN on the UNIQUE `applications.unti_id`), the child card opens the
 * same data as a popover, the five filters (incl. «Не найдено в наших
 * данных») ride the additive #36 params, the widget counts the unlinked rows,
 * and «Обновить данные сейчас» arms #35 right here, polling #23.unti2035
 * until the sweep clock advances.
 *
 * ADDITIVE (v1.9.42, T.Z153) — the «Итоговое испытание» column (the mirror's
 * `cof_exit_exam_status`): the compact RU label of the owner's dictionary in
 * the cell, the full text + the raw code in its title, «—» for NULL/unknown;
 * the `?exitExamStatus=` select in the mirror filter group; and the filter bar
 * split into the TWO named groups («Фильтр по данным УНТИ» over the mirror,
 * «Фильтр по данным cb-manager» over our join) — a layout change only, the
 * gray `col-our` tint on the four our columns is that group's visual twin.
 *
 * ADMIN floor end-to-end: the nav offers the item to the ADMIN only, the route
 * guards with `minRole="ADMIN"`, and #36/#37 answer an ADMIN session only —
 * the mirror carries `pgu_id`.
 *
 * ADDITIVE (v1.9.88, T.Z238) — the «Трансферт» select joins the «Фильтр по
 * данным УНТИ» group: the RAW mirror row's OWN `transfert_status`, the #36.1
 * facet's values verbatim + «Не указан» for the NULL/'' rows, the api answers
 * `?transfert=` (exact match, `none` for the bucket, 400 for a word the mirror
 * does not carry). The register's T.Z237 filter, pointed at the mirror column
 * — which is why the option list reads the page's OWN facet, never #3.1.
 */
export function UntiApplicationsPage() {
  const { params, setParams, reset } = useQueryParams();

  const filters = useMemo(() => parseFilters(params), [params]);
  const query = useMemo(() => filtersToQuery(filters), [filters]);
  const drawerId = parseIntParam(params[PARAM.ID]);

  const list = useUntiApplications(query);
  // #23 — the widget fold. The page polls it like the Settings panel does; the
  // block is OPTIONAL on the wire, and absence reads «нет данных», never a crash.
  const status = useSystemStatus();
  const unti2035 = foldUnti2035(status.data);
  const programs = usePrograms();

  // ---- «Обновить данные сейчас» (#35) — armed HERE, not only in Settings ----
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const baseline = useRef<{ lastRunAt: string | null; state: string } | null>(null);
  const armedAt = useRef(0);
  const run = useMutation({ mutationFn: () => requestUntiSyncRun() });

  const onRefresh = () => {
    setSyncNote(null);
    baseline.current = unti2035 ? { lastRunAt: unti2035.lastRunAt, state: unti2035.state } : null;
    armedAt.current = Date.now();
    run.mutate(undefined, {
      onSuccess: () => {
        setSyncNote('идёт синхронизация…');
        setSyncing(true);
        void status.refetch();
      },
      onError: (error) => {
        const code = typeof (error as { code?: unknown }).code === 'string' ? String((error as { code?: unknown }).code) : '';
        setSyncNote(code === 'UNTI_SYNC_RUN_PENDING' ? 'уже идёт' : 'Не удалось запустить синк. Попробуйте ещё раз.');
      },
    });
  };

  // While armed: a fast manual poll of #23 on top of its own 30 s beat.
  useEffect(() => {
    if (!syncing) return;
    const timer = window.setInterval(() => {
      if (Date.now() - armedAt.current > SYNC_POLL_MAX_MS) {
        setSyncing(false);
        setSyncNote('Синк всё ещё идёт — данные обновятся позже.');
        return;
      }
      void status.refetch();
    }, SYNC_POLL_MS);
    return () => window.clearInterval(timer);
  }, [syncing, status]);

  // The sweep is done when its clock advanced past the armed baseline (or the
  // state flipped) — then the table re-reads #36 with the fresh mirror.
  useEffect(() => {
    if (!syncing || !unti2035) return;
    const base = baseline.current;
    if (base !== null && unti2035.lastRunAt === base.lastRunAt && unti2035.state === base.state) return;
    setSyncing(false);
    setSyncNote('Данные обновлены.');
    void list.refetch();
  }, [syncing, unti2035, list]);

  const setFilter = (key: string, value: ParamValue) =>
    setParams(key === PARAM.PAGE || key === PARAM.PAGE_SIZE ? { [key]: value } : { [key]: value, [PARAM.PAGE]: undefined });

  const setPage = (page: number) => setParams({ [PARAM.PAGE]: page > 1 ? page : undefined });
  // The shared pager has no size select (the register's local pager is the one
  // that grew one); `?pageSize=` stays hand-editable through the URL.

  // One header click, one URL write — the register's T.Z59 rule. T.Z154: the
  // click is a SERVER sort now, so it also lands the user back on page 1 (the
  // dashboard's T.Z145 idiom) — the reordered set has its own first page.
  const toggleColumnSort = (columnId: SortableColumn) => {
    const next = formatSortState(toggleSort(filters.sort, columnId));
    setParams({ ...next, [PARAM.PAGE]: undefined });
  };

  // The slice the api ordered — the page never reorders it (T.Z154).
  const rows = useMemo(() => list.data?.data.items ?? [], [list.data]);
  const meta = list.data?.meta;
  const total = meta?.total ?? 0;
  const notFoundInOurData = meta?.notFoundInOurData;
  const pages = pageCount(total, filters.pageSize);
  const page = clampPage(filters.page, total, filters.pageSize);

  // «Курс» caption lookup: the programmes dictionary is the authority for the id
  // space the mirror's TEXT courseId speaks; the rows' own ids are the fallback
  // vocabulary while the dictionary has not answered (or no longer knows an id).
  const courseCaptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const program of programs.data?.data ?? []) {
      map.set(String(program.courseId), program.shortName || program.name);
    }
    for (const row of rows) {
      if (row.courseId !== null && !map.has(row.courseId)) map.set(row.courseId, row.courseId);
    }
    return map;
  }, [programs.data, rows]);

  // The statuses the loaded page shows — the source of the «observed» options
  // the status select grows when their vocabulary drifts (see statusFilterOptions).
  const observedStatuses = useMemo(() => [...new Set(rows.map((row) => row.status))], [rows]);

  const openDrawer = (row: UntiApplicationDto) => setParams({ [PARAM.ID]: String(row.id) });
  const closeDrawer = () => setParams({ [PARAM.ID]: undefined });

  return (
    <>
      <div className="unti-page">
        <div className="page-header">
          <div>
            <h1>Данные УНТИ</h1>
            <p className="page-header__sub">
              Зеркало заявлений cat.2035.university: как их отдал API — без преобразований.
            </p>
          </div>
          {/* T.Z147 — the manual refresh, ON the page (contract #35). The
              Settings panel keeps its own button; this one answers on 202 with
              «идёт синхронизация…» and watches #23.unti2035 until the sweep
              clock advances, on 409 with «уже идёт». */}
          <div className="unti-refresh" data-testid="unti-refresh">
            <p className="unti-refresh__clock" data-testid="unti-refresh-clock">
              {unti2035?.lastRunAt ? `Последнее обновление: ${formatDateTime(unti2035.lastRunAt)}` : 'Последнее обновление: —'}
            </p>
            <p className="unti-refresh__line" data-testid="unti-refresh-line">
              <Button size="sm" variant="secondary" data-testid="unti-refresh-button" onClick={onRefresh} disabled={run.isPending || syncing}>
                {syncing ? 'Синхронизируем…' : run.isPending ? 'Запускаем…' : 'Обновить данные сейчас'}
              </Button>
              {syncNote ? (
                <span className="unti-refresh__note" data-testid="unti-refresh-note" data-syncing={syncing || undefined}>
                  {syncNote}
                </span>
              ) : null}
            </p>
          </div>
        </div>

        {/* T.Z124's own precedent (mailings.css): the tiles wear the app's
            stat-card grammar under THIS page's classes — a real card, a 28px
            accent bar per tone, the count display-size — tokens only, so the
            dark theme is free and nothing here leans on dashboard.css being
            loaded. */}
        <section className="unti-widgets" aria-label="Сводка зеркала УНТИ" data-testid="unti-widgets">
          {untiWidgets(unti2035, notFoundInOurData).map((widget) => {
            const body = (
              <>
                <span className="unti-stat__bar" aria-hidden="true" />
                <span className="unti-stat__label">{widget.label}</span>
                <span className={widget.testId === 'unti-widget-sync' ? 'unti-stat__value unti-stat__value--date' : 'unti-stat__value'} data-testid={`${widget.testId}-value`}>
                  {widget.value}
                </span>
              </>
            );
            return widget.href ? (
              <Link key={widget.testId} to={widget.href} className={`unti-stat unti-stat--${widget.tone}`} data-testid={widget.testId} title={widget.hint}>
                {body}
              </Link>
            ) : (
              <div key={widget.testId} className={`unti-stat unti-stat--${widget.tone}`} data-testid={widget.testId} title={widget.hint}>
                {body}
              </div>
            );
          })}
        </section>

        <div className="card card--flush unti-card">
          <UntiFilterBar
            params={params}
            observedStatuses={observedStatuses}
            courseCaptions={courseCaptions}
            onFilter={setFilter}
            onReset={reset}
          />

          {list.isPending ? (
            <TableSkeleton />
          ) : list.isError ? (
            <div className="unti-toolbar-meta">
              <ErrorState
                message={list.error instanceof Error ? list.error.message : 'Не удалось загрузить данные УНТИ.'}
                onRetry={() => void list.refetch()}
              />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title="Записей не найдено"
              hint={
                hasActiveFilters(filters)
                  ? 'По этим фильтрам ничего нет — ослабьте условие или сбросьте фильтры.'
                  : 'Зеркало наполнит синк «Синк УНТИ (API)» — запустите его на странице «Настройки».'
              }
              action={
                hasActiveFilters(filters) ? (
                  <Button size="sm" data-testid="empty-reset" onClick={reset}>
                    Сбросить фильтры
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <div className="unti-toolbar-meta">
                <span data-testid="unti-total">{formatCount(total)} записей</span>
                <span data-testid="unti-page" aria-live="polite">
                  Страница {page} из {pages}
                </span>
                {!isDefaultSort(filters.sort) ? <span title={SERVER_SORT_HINT}>сортировка по всем записям</span> : null}
                {list.isFetching && !list.isPending ? <span aria-hidden="true">обновление…</span> : null}
              </div>

              <TableWrap>
                <table className="data" data-testid="unti-table">
                  <thead>
                    <tr>
                      {TABLE_HEADS.map((head) => {
                        const ourClass = head.our ? ' col-our' : '';
                        if (head.id === undefined) {
                          return (
                            <th key={head.testId ?? head.label} className={ourClass.trim() || undefined} data-testid={head.testId}>
                              {head.label}
                            </th>
                          );
                        }
                        const column = head.id;
                        const arrow = sortArrow(column, filters.sort);
                        return (
                          <th
                            key={column}
                            className={(head.num ? 'num' : '') + ourClass || undefined}
                            aria-sort={arrow === 'asc' ? 'ascending' : arrow === 'desc' ? 'descending' : undefined}
                          >
                            <button
                              type="button"
                              className={arrow ? 'th-sort th-sort--active' : 'th-sort'}
                              data-testid={`sort-${column}`}
                              data-order={arrow ?? undefined}
                              title={SERVER_SORT_HINT}
                              onClick={() => toggleColumnSort(column)}
                            >
                              <span>{head.label}</span>
                              <span className={arrow ? 'th-sort__arrow' : 'th-sort__arrow th-sort__arrow--idle'} aria-hidden="true">
                                {arrow === 'asc' ? '↑' : arrow === 'desc' ? '↓' : '↕'}
                              </span>
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const our = row.our ?? null;
                      const statusView = ourStatusView(our);
                      const contractView = ourContractView(our);
                      const formatLabel = ourFormatLabel(our);
                      const eviFailedLabel = ourEviFailedLabel(our);
                      // T.Z153 — the compact RU label of the mirror's exam status,
                      // the full dictionary text + the raw code in the title.
                      const exitExam = exitExamStatusView(row.cofExitExamStatus);
                      return (
                        <tr
                          key={row.id}
                          className="unti-row"
                          data-testid="unti-row"
                          data-unti-id={row.untiId}
                          data-linked={our ? 'true' : 'false'}
                          tabIndex={0}
                          onClick={() => openDrawer(row)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openDrawer(row);
                            }
                          }}
                        >
                          <td className="cell-primary">
                            <button
                              type="button"
                              className="link-btn"
                              data-testid="unti-row-link"
                              onClick={(event) => {
                                event.stopPropagation();
                                openDrawer(row);
                              }}
                            >
                              {row.untiId}
                            </button>
                          </td>
                          <td data-testid="row-course">{courseCaptions.get(row.courseId ?? '') ?? '—'}</td>
                          <td data-testid="row-status" data-status={row.status}>
                            <Badge tone={statusTone(row.status)} title="Статус ровно как его отдал API">
                              {row.status === '' ? '—' : row.status}
                            </Badge>
                          </td>
                          <td data-testid="row-our-status" className="col-our">
                            {statusView ? (
                              <Badge tone={statusView.tone} title={statusView.title} testId="badge-our-status">
                                {statusView.label}
                              </Badge>
                            ) : (
                              <span className="cell-muted">—</span>
                            )}
                          </td>
                          <td data-testid="row-our-format" className={`col-our${formatLabel ? '' : ' cell-muted'}`}>
                            {formatLabel ?? '—'}
                          </td>
                          <td data-testid="row-our-contract" className="col-our">
                            {contractView ? (
                              <Badge tone={contractView.tone} testId="badge-our-contract">
                                {contractView.label}
                              </Badge>
                            ) : (
                              <span className="cell-muted">—</span>
                            )}
                          </td>
                          <td data-testid="row-our-evi" className="col-our">
                            {eviFailedLabel ? (
                              <Badge tone="bad" testId="badge-our-evi">
                                {eviFailedLabel}
                              </Badge>
                            ) : (
                              <span className="cell-muted">—</span>
                            )}
                          </td>
                          <td className={row.transfertStatus ? undefined : 'cell-muted'}>{row.transfertStatus ?? '—'}</td>
                          <td className="cell-compact">{formatDateTime(row.appliedAt)}</td>
                          <td className="cell-compact">{formatDateTime(row.endDt)}</td>
                          <td className="cell-compact">{formatDateTime(row.untillDt)}</td>
                          {/* T.Z153 — «Итоговое испытание»: the compact dictionary label,
                              the full text + the raw code in the title; NULL/unknown → «—»
                              (an unknown spelling keeps the raw value in the title). */}
                          <td
                            data-testid="row-exit-exam"
                            className={`cell-exit-exam${exitExam?.known ? '' : ' cell-muted'}`}
                            data-known={exitExam?.known === false ? 'false' : undefined}
                            title={exitExam?.title}
                          >
                            {exitExam ? exitExam.label : '—'}
                          </td>
                          <td className="unti-our-cell" data-testid="row-our-card">
                            <OurDataCard our={our} untiId={row.untiId} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableWrap>

              <Pagination
                meta={{ page, totalPages: pages, total, limit: filters.pageSize }}
                onPage={setPage}
              />
            </>
          )}
        </div>
      </div>

      {drawerId !== undefined ? <UntiDetailDrawer id={drawerId} courseCaptions={courseCaptions} onClose={closeDrawer} /> : null}
    </>
  );
}

/** The OPTIONAL `unti2035` block of #23, read tolerantly (absent → no widgets numbers). */
function foldUnti2035(payload: unknown): Unti2035StatusDto | undefined {
  if (payload === null || typeof payload !== 'object') return undefined;
  const data = (payload as Record<string, unknown>)['data'];
  if (data === null || typeof data !== 'object') return undefined;
  const unti2035 = (data as Record<string, unknown>)['unti2035'];
  if (unti2035 === null || typeof unti2035 !== 'object') return undefined;
  return unti2035 as Unti2035StatusDto;
}

/** Narrowed for the empty-state hint (page-aware, pagination keys ignored). */
function hasActiveFilters(filters: ReturnType<typeof parseFilters>): boolean {
  return (
    filters.courseId !== undefined ||
    filters.status !== undefined ||
    filters.transfert !== undefined ||
    filters.untiId !== undefined ||
    filters.search !== undefined ||
    filters.exitExamStatus !== undefined ||
    hasOurFilters(filters)
  );
}

interface FilterBarProps {
  params: Record<string, string>;
  /** The raw statuses the loaded page shows — the unknown-vocabulary source. */
  observedStatuses: ReadonlyArray<string>;
  /** The course captions (#21 dictionary + the page's own rows). */
  courseCaptions: ReadonlyMap<string, string>;
  onFilter: (key: string, value: ParamValue) => void;
  onReset: () => void;
}

/**
 * The filter bar, in TWO named groups (T.Z153) — the layout twin of the data
 * split the page has had since v1.9.40:
 *
 *   * «Фильтр по данным УНТИ» — the MIRROR conditions: курс (dictionary
 *     labels), статус (raw values), the «Итоговое испытание» status
 *     (v1.9.42), unti_id, поиск;
 *   * «Фильтр по данным cb-manager» — the OUR-application conditions: наш
 *     статус, формат, договор, «Провалил ЕВИ» and the join state («Не найдено
 *     в наших данных»).
 *
 * Every control writes straight into the URL (the request semantics are
 * unchanged — one #36 read either way). Selects commit on change; the
 * free-text fields commit on Enter or blur (the register's rule — a request
 * per keystroke would hammer the api).
 */
function UntiFilterBar({ params, observedStatuses, courseCaptions, onFilter, onReset }: FilterBarProps) {
  const anyActive =
    params[PARAM.COURSE_ID] !== undefined ||
    params[PARAM.STATUS] !== undefined ||
    params[PARAM.TRANSFERT] !== undefined ||
    params[PARAM.UNTI_ID] !== undefined ||
    params[PARAM.SEARCH] !== undefined ||
    params[PARAM.EXIT_EXAM] !== undefined ||
    params[PARAM.OUR_STATUS] !== undefined ||
    params[PARAM.FORMAT] !== undefined ||
    params[PARAM.CONTRACT] !== undefined ||
    params[PARAM.EVI_FAILED] !== undefined ||
    params[PARAM.LINKED] !== undefined;

  return (
    <div className="unti-toolbar" role="search" aria-label="Фильтры данных УНТИ">
      {UNTI_FILTER_GROUPS.map((group) => (
        <section
          key={group.key}
          className={`unti-toolbar__group unti-toolbar__group--${group.key}`}
          data-testid={`filter-group-${group.key}`}
          aria-label={group.title}
        >
          <p className="unti-toolbar__group-title" data-testid="filter-group-title" title={group.hint}>
            {group.title}
          </p>
          <div className="unti-toolbar__row">
            {group.key === 'unti' ? (
              <>
                <div className="unti-toolbar__field">
                  <label className="unti-toolbar__label" htmlFor="unti-course">
                    Курс
                  </label>
                  <Select
                    id="unti-course"
                    data-testid="filter-course"
                    value={params[PARAM.COURSE_ID] ?? ''}
                    onChange={(event) => onFilter(PARAM.COURSE_ID, event.target.value || undefined)}
                  >
                    <option value="">Все</option>
                    {[...courseCaptions.entries()].map(([id, caption]) => (
                      <option key={id} value={id}>
                        {caption === id ? `Курс ${id}` : `${caption} (${id})`}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="unti-toolbar__field">
                  <label className="unti-toolbar__label" htmlFor="unti-status">
                    Статус (как в API)
                  </label>
                  <Select
                    id="unti-status"
                    data-testid="filter-status"
                    value={params[PARAM.STATUS] ?? ''}
                    onChange={(event) => onFilter(PARAM.STATUS, event.target.value || undefined)}
                  >
                    {statusFilterOptions(observedStatuses, params[PARAM.STATUS]).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>

                {/* ADDITIVE (v1.9.88, T.Z238) — the «Трансферт» word of the RAW
                    mirror row: the #36.1 facet's distinct values verbatim (their
                    vocabulary, never translated — the cell prints the same
                    string) plus the api's own `none` bucket («Не указан») for
                    the NULL/'' rows. The mirror filter group's twin of the
                    register's T.Z237 select — pointed at the mirror column, not
                    the winner fold. */}
                <UntiTransfertFilter params={params} onFilter={onFilter} />

                {/* T.Z153 — the «Итоговое испытание» status of the mirror row. */}
                <div className="unti-toolbar__field">
                  <label className="unti-toolbar__label" htmlFor="unti-exit-exam">
                    {EXIT_EXAM_COLUMN}
                  </label>
                  <Select
                    id="unti-exit-exam"
                    data-testid="filter-exit-exam"
                    value={params[PARAM.EXIT_EXAM] ?? ''}
                    onChange={(event) => onFilter(PARAM.EXIT_EXAM, event.target.value || undefined)}
                  >
                    {exitExamFilterOptions(params[PARAM.EXIT_EXAM]).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="unti-toolbar__field unti-toolbar__field--narrow">
                  <label className="unti-toolbar__label" htmlFor="unti-unti-id">
                    УНТИ ID
                  </label>
                  <CommitInput
                    id="unti-unti-id"
                    testId="filter-unti-id"
                    value={params[PARAM.UNTI_ID] ?? ''}
                    placeholder="точно"
                    onCommit={(value) => onFilter(PARAM.UNTI_ID, value)}
                  />
                </div>

                <div className="unti-toolbar__field unti-toolbar__field--q">
                  <label className="unti-toolbar__label" htmlFor="unti-search">
                    Поиск
                  </label>
                  <CommitInput
                    id="unti-search"
                    testId="filter-search"
                    value={params[PARAM.SEARCH] ?? ''}
                    placeholder="по unti_id и pgu_id"
                    onCommit={(value) => onFilter(PARAM.SEARCH, value)}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="unti-toolbar__field">
                  <label className="unti-toolbar__label" htmlFor="unti-our-status">
                    Наш статус
                  </label>
                  <Select
                    id="unti-our-status"
                    data-testid="filter-our-status"
                    value={params[PARAM.OUR_STATUS] ?? ''}
                    onChange={(event) => onFilter(PARAM.OUR_STATUS, event.target.value || undefined)}
                  >
                    {ourStatusFilterOptions(params[PARAM.OUR_STATUS]).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="unti-toolbar__field">
                  <label className="unti-toolbar__label" htmlFor="unti-format">
                    Формат
                  </label>
                  <Select
                    id="unti-format"
                    data-testid="filter-our-format"
                    value={params[PARAM.FORMAT] ?? ''}
                    onChange={(event) => onFilter(PARAM.FORMAT, event.target.value || undefined)}
                  >
                    <option value="">Все</option>
                    <option value="OFFLINE">Офлайн</option>
                    <option value="ONLINE">Онлайн</option>
                  </Select>
                </div>

                <div className="unti-toolbar__field">
                  <label className="unti-toolbar__label" htmlFor="unti-contract">
                    Договор
                  </label>
                  <Select
                    id="unti-contract"
                    data-testid="filter-our-contract"
                    value={params[PARAM.CONTRACT] ?? ''}
                    onChange={(event) => onFilter(PARAM.CONTRACT, event.target.value || undefined)}
                  >
                    <option value="">Все</option>
                    {OUR_CONTRACT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="unti-toolbar__field">
                  <label className="unti-toolbar__label" htmlFor="unti-evi-failed">
                    Провалил ЕВИ
                  </label>
                  <Select
                    id="unti-evi-failed"
                    data-testid="filter-our-evi"
                    value={params[PARAM.EVI_FAILED] ?? ''}
                    onChange={(event) => onFilter(PARAM.EVI_FAILED, event.target.value || undefined)}
                  >
                    <option value="">Все</option>
                    <option value="true">Провалил</option>
                    <option value="false">Не провалил</option>
                  </Select>
                </div>

                <div className="unti-toolbar__field">
                  <label className="unti-toolbar__label" htmlFor="unti-linked">
                    Наши данные
                  </label>
                  <Select
                    id="unti-linked"
                    data-testid="filter-linked"
                    value={params[PARAM.LINKED] ?? ''}
                    onChange={(event) => onFilter(PARAM.LINKED, event.target.value || undefined)}
                  >
                    <option value="">Все</option>
                    <option value="true">Есть в наших данных</option>
                    <option value="false">Не найдено в наших данных</option>
                  </Select>
                </div>
              </>
            )}
          </div>
        </section>
      ))}

      {anyActive ? (
        <div className="unti-toolbar__row unti-toolbar__row--reset">
          <Button size="sm" variant="ghost" data-testid="filter-reset" onClick={onReset}>
            Сбросить
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * ADDITIVE (v1.9.88, T.Z238) — the «Трансферт» select of the mirror group: the
 * #36.1 facet's DISTINCT words over the RAW mirror rows (`useUntiTransfertValues`,
 * the register's quiet-refusal profile — a failing read degrades to the `none`
 * option alone, the page otherwise untouched), the values verbatim (their
 * vocabulary, never translated — the cell prints the same string), plus the
 * api's own `none` literal («Не указан») for the NULL/'' rows. A value the URL
 * carries that the facet does not know stays selectable as itself — a filter
 * must never read as «nothing selected». The register's T.Z237 component shape,
 * pointed at the mirror column (NOT the winner fold) — which is why this page
 * reads its OWN facet, #36.1, and never #3.1.
 */
function UntiTransfertFilter({
  params,
  onFilter,
}: {
  params: Record<string, string>;
  onFilter: (key: string, value: ParamValue) => void;
}) {
  const values = useUntiTransfertValues();
  const value = params[PARAM.TRANSFERT] ?? '';
  return (
    <div className="unti-toolbar__field">
      <label className="unti-toolbar__label" htmlFor="unti-transfert">
        Трансферт
      </label>
      <Select
        id="unti-transfert"
        data-testid="filter-transfert"
        value={value}
        onChange={(event) => onFilter(PARAM.TRANSFERT, event.target.value || undefined)}
      >
        <option value="">Все</option>
        <option value={TRANSFERT_FILTER_NONE}>{TRANSFERT_NONE_LABEL}</option>
        {untiTransfertSelectOptions(values.data ?? [], params[PARAM.TRANSFERT]).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
}

/** The free-text commit input of the register's bar (Enter or blur commits). */
function CommitInput({
  id,
  testId,
  value,
  placeholder,
  onCommit,
}: {
  id: string;
  testId: string;
  value: string;
  placeholder?: string;
  onCommit: (value: string | undefined) => void;
}) {
  const commit = (raw: string) => onCommit(raw.trim() === '' ? undefined : raw.trim());
  return (
    <Input
      key={value}
      id={id}
      data-testid={testId}
      defaultValue={value}
      placeholder={placeholder}
      onBlur={(event) => commit(event.currentTarget.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit(event.currentTarget.value);
        }
      }}
    />
  );
}

/**
 * The DETAILS drawer (#37): the row's 39 fields in the five owner groups, the
 * raw status pill, the change history, and the link to OUR register.
 */
function UntiDetailDrawer({
  id,
  courseCaptions,
  onClose,
}: {
  id: number;
  courseCaptions: ReadonlyMap<string, string>;
  onClose: () => void;
}) {
  const detail = useUntiApplicationDetail(id);
  const row = detail.data?.data;

  return (
    <Drawer open onClose={onClose} title={`Заявление УНТИ № ${id}`} description="Все поля зеркала, как их отдал API, и история изменений между синками.">
      {detail.isPending ? (
        <p className="unti-detail__note">Загрузка…</p>
      ) : detail.isError ? (
        <p className="unti-detail__error" data-testid="unti-detail-error">
          {detail.error instanceof Error ? detail.error.message : 'Не удалось загрузить заявление.'}
        </p>
      ) : row === undefined ? null : (
        <div className="unti-detail" data-testid="unti-detail">
          <div className="unti-detail__head">
            <StatusPill status={row.status} />
            <span className="unti-detail__freshness" data-testid="unti-detail-freshness">
              в зеркале с {formatDateTime(row.firstSeenAt)} · синк {formatDateTime(row.lastSeenAt)}
            </span>
            {row.untiId ? (
              <Link className="unti-detail__register-link" data-testid="unti-register-link" to={applicationsRegisterLink(row.untiId)}>
                Открыть в реестре →
              </Link>
            ) : null}
          </div>

          {UNTI_FIELD_GROUPS.map((group) => (
            <section key={group.key} className="unti-detail__group" data-testid={`unti-group-${group.key}`}>
              <h3 className="unti-detail__group-title">{group.title}</h3>
              <div className="unti-detail__grid">
                {group.fields.map((spec) => {
                  const value = row[spec.field];
                  // A datetime-ish value renders as `ДД.ММ.ГГГГ ЧЧ:ММ` with the
                  // verbatim wire string in the title — readable, still honest.
                  const printable =
                    spec.field === 'courseId' && value !== null && value !== ''
                      ? courseCaption(courseCaptions, value)
                      : typeof value === 'string' && looksLikeDatetime(value)
                        ? formatDateTime(value)
                        : formatFieldValue(value);
                  return (
                    <div key={spec.field} className={spec.wide ? 'unti-detail__field unti-detail__field--wide' : 'unti-detail__field'}>
                      <span className="unti-detail__field-label">
                        {spec.label} <span className="unti-detail__field-api">{spec.api}</span>
                      </span>
                      <span className="unti-detail__field-value" data-field={spec.api} title={value ?? undefined}>
                        {printable}
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}

          <section className="unti-detail__group" data-testid="unti-group-history">
            <h3 className="unti-detail__group-title">
              История изменений <span className="unti-detail__history-count">{row.changes.length}</span>
            </h3>
            {row.changes.length === 0 ? (
              <p className="unti-detail__note">Изменений между синками не зафиксировано.</p>
            ) : (
              <ul className="unti-timeline">
                {[...row.changes].reverse().map((change) => (
                  <UntiChangeRow key={change.id} change={change} />
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}

/** «ИИ 8-9 (31601)» — the caption plus the raw id, so the mirror stays verbatim. */
function courseCaption(captions: ReadonlyMap<string, string>, courseId: string): string {
  const caption = captions.get(courseId);
  return caption === undefined || caption === courseId ? courseId : `${caption} (${courseId})`;
}

function StatusPill({ status }: { status: string }) {
  return (
    <Badge tone={statusTone(status)} testId="unti-detail-status">
      {status === '' ? '—' : status}
    </Badge>
  );
}

/** One history row — field, old → new, when, and the sweep that saw it. Newest first. */
function UntiChangeRow({ change }: { change: UntiApplicationChangeDto }) {
  return (
    <li className="unti-timeline__row" data-testid="unti-change" data-field={change.field}>
      <span className="unti-timeline__when">{formatDateTime(change.seenAt)}</span>
      <span className="unti-timeline__body">
        <span className="unti-timeline__field">{change.field}</span>
        <span className="unti-timeline__change">
          {formatChangeValue(change.oldValue)} <span aria-hidden="true">→</span> {formatChangeValue(change.newValue)}
        </span>
        <span className="unti-timeline__sweep">{change.sweepId}</span>
      </span>
    </li>
  );
}

/** Skeleton of the table it stands in for (the filter bar stays mounted). */
function TableSkeleton() {
  return (
    <div className="table-wrap" data-testid="unti-skeleton">
      <table className="data" aria-hidden="true">
        <thead>
          <tr>
            {TABLE_HEADS.map((head) => (
              <th key={head.id ?? head.testId ?? head.label} className={head.num ? 'num' : undefined}>
                {head.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }, (_, index) => (
            <tr key={index}>
              {TABLE_HEADS.map((head) => (
                <td key={head.id ?? head.testId ?? head.label}>
                  <div className="skeleton skeleton--line" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * T.Z147 — the child card: a small icon button per row that opens a READ-ONLY
 * popover with OUR data for that child (наш статус, формат, договор, ЕВИ).
 * The register's drawer is the heavy read (#4: groups, history, journal);
 * here the data is already on the row (`our` block of #36), so the card costs
 * no request and closes on the backdrop click. `null` our-data renders a
 * muted «нет в наших данных» mark instead of a dead button.
 */
function OurDataCard({ our, untiId }: { our: UntiApplicationOurDataDto | null; untiId: string }) {
  const [open, setOpen] = useState(false);

  if (!our) {
    return (
      <span className="unti-our-card__none" data-testid="our-card-none" title="Этот unti_id не найден в наших данных">
        нет в наших данных
      </span>
    );
  }

  const statusView = ourStatusView(our);
  const contractView = ourContractView(our);
  const formatLabel = ourFormatLabel(our);
  const eviFailedLabel = ourEviFailedLabel(our);

  return (
    <span className="unti-our-card">
      <button
        type="button"
        className="unti-our-card__toggle"
        data-testid="our-card-toggle"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Наши данные по этому ребёнку"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        👤
      </button>
      {open ? (
        <>
          {/* the backdrop swallows the next click anywhere — one exit, no focus trap */}
          <span
            className="unti-our-card__backdrop"
            data-testid="our-card-backdrop"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
            }}
          />
          <span
            className="unti-our-card__pop"
            role="dialog"
            aria-label="Наши данные ребёнка"
            data-testid="our-card"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="unti-our-card__title">Наши данные</span>
            <span className="unti-our-card__row" data-testid="our-card-status">
              <span className="unti-our-card__label">Наш статус</span>
              {statusView ? (
                <Badge tone={statusView.tone} title={statusView.title}>
                  {statusView.label}
                </Badge>
              ) : (
                <span className="cell-muted">—</span>
              )}
            </span>
            <span className="unti-our-card__row" data-testid="our-card-format">
              <span className="unti-our-card__label">Формат</span>
              <span className={formatLabel ? undefined : 'cell-muted'}>{formatLabel ?? '—'}</span>
            </span>
            <span className="unti-our-card__row" data-testid="our-card-contract">
              <span className="unti-our-card__label">Договор</span>
              {contractView ? <Badge tone={contractView.tone}>{contractView.label}</Badge> : <span className="cell-muted">—</span>}
            </span>
            <span className="unti-our-card__row" data-testid="our-card-evi">
              <span className="unti-our-card__label">ЕВИ</span>
              {eviFailedLabel ? <Badge tone="bad">{eviFailedLabel}</Badge> : <span className="cell-muted">—</span>}
            </span>
            <Link className="unti-our-card__link" data-testid="our-card-register-link" to={applicationsRegisterLink(untiId)} onClick={() => setOpen(false)}>
              Открыть в реестре →
            </Link>
          </span>
        </>
      ) : null}
    </span>
  );
}
